(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  let mutationQueue = Promise.resolve();
  let refreshPromise = null;

  const getDrawer = () => document.querySelector('theme-drawer#cart-drawer');

  const getConfig = () => {
    const script = getDrawer()?.querySelector('[data-skovn-cart-config]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('[skovn-cart] Invalid drawer configuration:', error);
      return {};
    }
  };

  const formatMoney = (cents, currencyOverride) => {
    const config = getConfig();
    const currency = currencyOverride || config.currency || 'GBP';
    const locale = config.locale || document.documentElement.lang || 'en-GB';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const fetchCart = async () => {
    const response = await fetch(`${ROOT}cart.js`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Unable to read cart (${response.status})`);
    return response.json();
  };

  const updateHeaderCount = (count) => {
    const itemCount = Math.max(0, Number(count) || 0);

    document.querySelectorAll('[data-skovn-cart-count]').forEach((node) => {
      node.textContent = itemCount > 0 ? ` (${itemCount})` : '';
    });

    document.querySelectorAll('[data-skovn-cart-badge]').forEach((node) => {
      node.hidden = itemCount === 0;
      node.textContent = itemCount > 99 ? '99+' : String(itemCount);
    });

    document.querySelectorAll('[ref="cartBubbleCount"]').forEach((node) => {
      node.textContent = itemCount < 100 ? String(itemCount) : '';
      node.classList.toggle('hidden', itemCount === 0);
    });

    document.querySelectorAll('[ref="cartBubble"]').forEach((node) => {
      node.classList.toggle('visually-hidden', itemCount === 0);
    });

    document.querySelectorAll('[aria-controls="cart-drawer"]').forEach((node) => {
      node.setAttribute('aria-label', itemCount > 0 ? `Open cart, ${itemCount} items` : 'Open cart');
    });

    sessionStorage.setItem('cart-count', JSON.stringify({ value: String(itemCount), timestamp: Date.now() }));
  };

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const makeChangeButton = ({ label, symbol, key, quantity, className = '' }) => {
    const button = makeElement('button', className, symbol);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.dataset.skovnCartChange = '';
    button.dataset.key = key;
    button.dataset.quantity = String(quantity);
    return button;
  };

  const makeCartItem = (item, currency) => {
    const article = makeElement('article', 'chadao-cart-item');
    article.dataset.cartKey = item.key;

    const imageLink = makeElement('a', 'chadao-cart-item__image');
    imageLink.href = item.url || '#';
    imageLink.setAttribute('aria-label', item.product_title || item.title || 'Product');

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.featured_image?.alt || item.product_title || item.title || '';
      image.width = 220;
      image.height = 220;
      image.loading = 'eager';
      imageLink.append(image);
    }

    const details = makeElement('div', 'chadao-cart-item__details');
    const topline = makeElement('div', 'chadao-cart-item__topline');

    const title = makeElement('a', 'chadao-cart-item__title', item.product_title || item.title || 'Product');
    title.href = item.url || '#';

    topline.append(title, makeElement('span', 'chadao-cart-item__price', formatMoney(item.final_line_price, currency)));
    details.append(topline);

    const options = (item.options_with_values || []).filter((option) => option?.value && option.value !== 'Default Title');

    if (options.length) {
      const optionList = makeElement('div', 'chadao-cart-item__options');

      options.forEach((option) => {
        const row = document.createElement('span');
        row.append(makeElement('strong', '', `${option.name}:`), document.createTextNode(` ${option.value}`));
        optionList.append(row);
      });

      details.append(optionList);
    }

    const controls = makeElement('div', 'chadao-cart-item__controls');
    const quantity = makeElement('div', 'chadao-cart-item__quantity');
    quantity.setAttribute('aria-label', 'Quantity');

    quantity.append(
      makeChangeButton({
        label: 'Decrease quantity',
        symbol: '\u2212',
        key: item.key,
        quantity: Math.max(0, item.quantity - 1),
      }),
      makeElement('span', '', String(item.quantity)),
      makeChangeButton({
        label: 'Increase quantity',
        symbol: '+',
        key: item.key,
        quantity: item.quantity + 1,
      })
    );

    const remove = makeChangeButton({
      label: `Remove ${item.product_title || item.title || 'product'}`,
      symbol: '',
      key: item.key,
      quantity: 0,
      className: 'chadao-cart-item__remove',
    });

    remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    controls.append(quantity, remove);
    details.append(controls);
    article.append(imageLink, details);

    return article;
  };

  const renderShipping = (cart) => {
    const progress = getDrawer()?.querySelector('[data-chadao-shipping-progress]');
    if (!progress) return;

    const threshold = Math.max(1, Number(progress.dataset.threshold) || 8500);
    const remaining = Math.max(0, threshold - Number(cart.total_price || 0));
    const percentage = Math.min(100, Math.max(0, (Number(cart.total_price || 0) / threshold) * 100));

    const message = progress.querySelector('[data-chadao-shipping-message]');
    const bar = progress.querySelector('[data-chadao-shipping-bar]');
    const progressBar = progress.querySelector('[role="progressbar"]');

    if (message) {
      if (remaining <= 0) {
        message.textContent = progress.dataset.successMessage || 'Free shipping unlocked';
      } else {
        const amount = formatMoney(remaining, cart.currency);
        const template = progress.dataset.progressMessage || 'You are [amount] away from free shipping';
        message.textContent = template
          .replaceAll('[amount]', amount)
          .replaceAll('{{ amount }}', amount)
          .replaceAll('{amount}', amount);
      }
    }

    if (bar instanceof HTMLElement) bar.style.width = `${percentage}%`;
    if (progressBar) progressBar.setAttribute('aria-valuenow', String(Math.round(Number(cart.total_price || 0) / 100)));
  };

  const renderRecommendations = (cart) => {
    const drawer = getDrawer();
    if (!drawer) return;

    const cartVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id)));

    drawer.querySelectorAll('[data-chadao-cart-rec-card]').forEach((card) => {
      card.hidden = cartVariantIds.has(String(card.dataset.variantId));
    });
  };

  const renderCart = (cart) => {
    const drawer = getDrawer();
    if (!drawer) return cart;

    const config = getConfig();
    const empty = Number(cart.item_count || 0) === 0;
    const panel = drawer.querySelector('dialog');
    const inner = drawer.querySelector('[data-skovn-cart-inner]');
    const heading = drawer.querySelector('[data-skovn-cart-heading]');
    const emptyState = drawer.querySelector('[data-skovn-cart-empty]');
    const filledState = drawer.querySelector('[data-skovn-cart-filled]');
    const footer = drawer.querySelector('[data-skovn-cart-footer]');
    const items = drawer.querySelector('[data-skovn-cart-items]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');

    drawer.dataset.cartEmpty = String(empty);
    panel?.setAttribute('data-skovn-cart-item-count', String(cart.item_count || 0));
    panel?.toggleAttribute('data-skovn-mobile-empty', empty);
    inner?.classList.toggle('cart-drawer--empty', empty);

    if (inner) inner.dataset.skovnRenderedCartCount = String(cart.item_count || 0);

    if (heading) {
      heading.textContent = empty
        ? config.emptyTitle || 'Your cart is empty'
        : `${config.drawerTitle || 'In Your Bag'} (${cart.item_count})`;
    }

    if (emptyState) emptyState.hidden = !empty;
    if (filledState) filledState.hidden = empty;
    if (footer) footer.hidden = empty;

    if (items && !empty) {
      items.replaceChildren(...cart.items.map((item) => makeCartItem(item, cart.currency)));
    } else if (items) {
      items.replaceChildren();
    }

    if (subtotal) subtotal.textContent = formatMoney(cart.total_price, cart.currency);

    renderShipping(cart);
    renderRecommendations(cart);
    updateHeaderCount(cart.item_count);

    document.dispatchEvent(new CustomEvent('skovn:cart:updated', { bubbles: true, detail: { cart } }));

    return cart;
  };

const replaceDrawerMarkup = (markup) => {
  if (!markup) return getDrawer();

  const currentDrawer = getDrawer();
  const currentSection = currentDrawer?.closest('#shopify-section-cart-drawer-section');
  if (!currentSection) return currentDrawer;

  const template = document.createElement('template');
  template.innerHTML = markup;

  const nextSection = template.content.querySelector('#shopify-section-cart-drawer-section');
  if (!nextSection) throw new Error('Cart drawer section was not returned by Shopify.');

  currentSection.replaceWith(nextSection);
  return getDrawer();
};

const refreshDrawerMarkup = async () => {
  const currentDrawer = getDrawer();
  if (!currentDrawer) return null;

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', 'cart-drawer-section');

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) throw new Error('Unable to refresh cart drawer (' + response.status + ').');
  return replaceDrawerMarkup(await response.text());
};

const open = async () => {
  const drawer = getDrawer();

  if (!drawer) {
    window.location.assign(ROOT + 'cart');
    return false;
  }

  await customElements.whenDefined('theme-drawer');

  try {
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('chadao-cart-open');

    if (!drawer.hasAttribute('open')) {
      if (typeof drawer.open === 'function') {
        drawer.open();
      } else if (typeof drawer.toggle === 'function') {
        drawer.toggle();
      } else {
        drawer.setAttribute('open', '');
        const panel = drawer.querySelector('dialog');
        if (panel && !panel.open && typeof panel.showModal === 'function') panel.showModal();
      }
    }

    return true;
  } catch (error) {
    console.error('[skovn-cart] Unable to open cart drawer:', error);
    return false;
  }
};

  const close = () => {
    const drawer = getDrawer();

    drawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('chadao-cart-open');

    if (typeof drawer?.close === 'function') drawer.close();
  };

  const refresh = ({ openAfter = false } = {}) => {
    if (!refreshPromise) {
      refreshPromise = fetchCart()
        .then(renderCart)
        .finally(() => {
          refreshPromise = null;
        });
    }

    return refreshPromise.then(async (cart) => {
      if (openAfter) await open();
      return cart;
    });
  };

  const queueMutation = (mutation) => {
    mutationQueue = mutationQueue.catch(() => undefined).then(mutation);
    return mutationQueue;
  };

  const showFormError = (form, error) => {
    const message = error instanceof Error ? error.message : 'This item could not be added.';
    const region = form.closest('product-form-component')?.querySelector('[ref="addToCartTextError"], [ref="liveRegion"]');

    if (region) {
      region.textContent = message;
      region.classList.remove('hidden');
    }
  };

  const addForm = (form, options = {}) => queueMutation(async () => {
    if (!(form instanceof HTMLFormElement)) throw new Error('Product form not found');

    const button = options.button || form.querySelector('[type="submit"]');
    const formData = new FormData(form);

    if (options.variantId) formData.set('id', String(options.variantId));
    if (options.quantity !== undefined) formData.set('quantity', String(options.quantity));

    // Shopify returns this freshly rendered drawer in the cart/add response.
    formData.set('sections', 'cart-drawer-section');
    formData.set('sections_url', window.location.pathname);

    if (!formData.get('id') && !Array.from(formData.keys()).some((key) => key.startsWith('items['))) {
      throw new Error('Please select an available option.');
    }

    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'This item could not be added.');
      }

      if (button) button.dataset.added = 'true';

const cart = await fetchCart();
renderCart(cart);

const drawerMarkup = result.sections?.['cart-drawer-section'];
if (drawerMarkup) {
  replaceDrawerMarkup(drawerMarkup);
} else {
  await refreshDrawerMarkup();
}

await open();

      return result;
    } catch (error) {
      showFormError(form, error);
      throw error;
    } finally {
      button?.removeAttribute('aria-busy');

      if (button) {
        button.disabled = false;
        window.setTimeout(() => button.removeAttribute('data-added'), 1200);
      }
    }
  });

  const addVariant = (variantId, quantity = 1, button = null) => {
    const form = document.createElement('form');

    const id = document.createElement('input');
    id.name = 'id';
    id.value = String(variantId);

    const qty = document.createElement('input');
    qty.name = 'quantity';
    qty.value = String(quantity);

    form.append(id, qty);

    return addForm(form, { button });
  };

  const changeLine = (identifier, quantity, button = null) => queueMutation(async () => {
    const safeQuantity = Math.max(0, Number(quantity));

    if (!identifier || !Number.isFinite(safeQuantity)) return null;

    const drawer = getDrawer();

    button?.setAttribute('aria-busy', 'true');
    drawer?.classList.add('is-cart-updating');

    try {
      const payload = /^\d+$/.test(String(identifier))
        ? { line: Number(identifier), quantity: safeQuantity }
        : { id: String(identifier), quantity: safeQuantity };

      const response = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const cart = await response.json();

      if (!response.ok || cart.status) {
        throw new Error(cart.description || cart.message || 'The cart could not be updated.');
      }

      renderCart(cart);
      return cart;
    } finally {
      button?.removeAttribute('aria-busy');
      drawer?.classList.remove('is-cart-updating');
    }
  });

  const isCartAddForm = (form) => {
    if (form.matches('[data-type="add-to-cart-form"], [data-skovn-quick-add]')) return true;

    try {
      return new URL(form.action, window.location.origin).pathname.replace(/\/$/, '').endsWith('/cart/add');
    } catch (_error) {
      return false;
    }
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;

      if (!form || !isCartAddForm(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const button = event.submitter instanceof HTMLElement ? event.submitter : form.querySelector('[type="submit"]');

      if (button?.hasAttribute('disabled')) return;

      button?.classList.add('is-loading');

      addForm(form, { button })
        .catch((error) => console.error('[skovn-cart] Unable to add product:', error))
        .finally(() => button?.classList.remove('is-loading'));
    },
    true
  );

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) return;

    const changeButton = target.closest('[data-skovn-cart-change]');

    if (changeButton) {
      event.preventDefault();

      changeLine(changeButton.dataset.key || changeButton.dataset.line, changeButton.dataset.quantity, changeButton)
        .catch((error) => console.error('[skovn-cart] Unable to update line:', error));

      return;
    }

    const recommendation = target.closest('[data-chadao-cart-rec-add]');

    if (recommendation) {
      event.preventDefault();

      addVariant(recommendation.dataset.variantId, 1, recommendation)
        .catch((error) => console.error('[skovn-cart] Unable to add recommendation:', error));

      return;
    }

    if (target.closest('[data-skovn-cart-close]')) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target !== getDrawer()) return;

    getDrawer()?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('chadao-cart-open');
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target !== getDrawer()) return;

    getDrawer()?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('chadao-cart-open');
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('#cart-drawer')) refresh().catch(() => undefined);
  });

  window.SkovnCart = {
    addForm,
    addVariant,
    changeLine,
    close,
    fetchCart,
    open,
    refresh,
    render: renderCart,

    async toggle() {
      const drawer = getDrawer();

      if (drawer?.hasAttribute('open')) {
        close();
        return;
      }

      await refresh({ openAfter: true });
    },
  };

  window.SkovnCartDrawerClose = close;

  window.addEventListener('pageshow', () => {
    refresh().catch((error) => console.warn('[skovn-cart] Unable to refresh cart:', error));
  });
})();